import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, Eyebrow } from "@/components/Reveal";
import CtaBand from "@/components/home/CtaBand";
import { SERVICES } from "@/data/content";

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="pt-36 sm:pt-44 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Services</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink leading-[1.05] max-w-3xl">
              Every kind of capital, <span className="font-editorial italic font-medium text-gold-600">one adviser.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-ink/60 max-w-2xl leading-relaxed">
              From a first home to a multi-lot development, Invesmo structures and places debt across the full spectrum.
              Select a discipline to see how we work — then book a meeting to discuss your scenario.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={0.04 * i}>
                <AccordionItem
                  value={s.slug}
                  data-testid={`service-accordion-${s.slug}`}
                  className="rounded-3xl border border-ink/8 bg-white px-6 sm:px-10 overflow-hidden transition-all duration-300 data-[state=open]:border-gold/50 data-[state=open]:shadow-lift"
                >
                  <AccordionTrigger data-testid={`service-trigger-${s.slug}`} className="hover:no-underline py-7">
                    <div className="flex items-center gap-5 text-left">
                      <span className="shrink-0 w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center">
                        <s.icon size={22} className="text-brand" strokeWidth={1.7} />
                      </span>
                      <div>
                        <div className="font-display text-xl sm:text-2xl font-bold text-ink">{s.title}</div>
                        <div className="text-sm text-ink/50 mt-1 hidden sm:block">{s.short}</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-2">
                    <p className="text-ink/60 leading-relaxed mb-6 sm:hidden">{s.short}</p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-8">
                      {s.points.map((p) => (
                        <div key={p} className="flex items-start gap-3 text-sm text-ink/70">
                          <Check size={15} className="text-brand mt-0.5 shrink-0" strokeWidth={2.5} />
                          {p}
                        </div>
                      ))}
                    </div>
                    <Link
                      to={`/book?service=${s.slug}`}
                      data-testid={`service-book-${s.slug}`}
                      className="group inline-flex items-center gap-2 rounded-full bg-obsidian text-gold px-6 py-3 text-sm font-semibold transition-all duration-300 hover:bg-gold hover:text-obsidian"
                    >
                      Discuss {s.title.toLowerCase()} with Anu
                      <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </AccordionContent>
                </AccordionItem>
              </Reveal>
            ))}
          </Accordion>

          <p className="text-[11px] text-ink/40 mt-8 leading-relaxed">
            *LMI waivers, preferential LVRs and professional pricing are subject to individual lender policy, eligibility
            criteria and credit assessment. All applications are subject to lender approval.
          </p>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
